import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMobileTheme } from '../../../hooks/useMobileTheme';
import type { MobileThemeKey } from '../../../hooks/useMobileTheme';
import RentalNoticeModal from '../../publish/components/RentalNoticeModal';
import { useNavHeight } from '../../../hooks/useNavHeight';

type ThemeKey = 'tech' | 'pink' | 'white';

interface MobileHeroProps {
  tenantMatchCount: number | null;
  onShowWaitingModal: () => void;
  onShowGuestOnboarding: () => void;
}

/* ─── 主題設定表 ─── */
const THEMES: Record<ThemeKey, {
  label: string;
  dot: string;
  bgImage: string;
  overlay: string;
  badgeBg: string;
  badgeBorder: string;
  badgeText: string;
  badgeIcon: string;
  accentText: string;
  titleText: string;
  subText: string;
  ctaPrimary: string;
  ctaSecondary: string;
  waitBorder: string;
  waitBg: string;
  waitTitle: string;
  waitSub: string;
  statsBg: string;
  statsBorder: string;
  statsDivide: string;
  statsNum: string;
  statsLabel: string;
  iconCardBg: string;
  iconCardBorder: string;
  iconCardHover: string;
  iconBg: string;
  iconText: string;
  iconLabel: string;
  featureCardClass: string;
  featureTitleClass: string;
  featureDescClass: string;
  featureArrowClass: string;
  sectionLabelClass: string;
  switcherBg: string;
  switcherIconColor: string;
  switcherActiveClass: (key: ThemeKey) => string;
  switcherInactiveClass: string;
}> = {

  /* ══ 科技深色 ══ */
  tech: {
    label: '科技',
    dot: 'bg-teal-400',
    bgImage: 'https://public.readdy.ai/ai/img_res/83eba4cd918fa4d346eb73fccf264e5e.jpg',
    overlay: 'bg-gradient-to-b from-black/65 via-black/50 to-black/70',
    badgeBg: 'bg-teal-400/25',
    badgeBorder: 'border-teal-300/35',
    badgeText: 'text-teal-100',
    badgeIcon: 'ri-sparkling-2-fill',
    accentText: 'text-teal-300',
    titleText: 'text-white',
    subText: 'text-white/70',
    ctaPrimary: 'bg-teal-500 hover:bg-teal-400 text-white',
    ctaSecondary: 'bg-white/15 hover:bg-white/25 text-white border border-white/30',
    waitBorder: 'border-amber-400/40 hover:border-amber-400/70',
    waitBg: 'bg-black/40 hover:bg-black/55',
    waitTitle: 'text-white',
    waitSub: 'text-white/50',
    statsBg: 'bg-white/8',
    statsBorder: 'border-white/15',
    statsDivide: 'divide-white/15',
    statsNum: 'text-teal-300',
    statsLabel: 'text-white/50',
    iconCardBg: 'bg-white/10',
    iconCardBorder: 'border-white/15',
    iconCardHover: 'hover:bg-white/18',
    iconBg: 'bg-teal-500/80 group-hover:bg-teal-500',
    iconText: 'text-white',
    iconLabel: 'text-white/80',
    featureCardClass: 'bg-white/8 border-white/12 hover:bg-white/14',
    featureTitleClass: 'text-white',
    featureDescClass: 'text-white/60',
    featureArrowClass: 'text-white/40',
    sectionLabelClass: 'text-white/35',
    switcherBg: 'bg-black/35 border-white/15',
    switcherIconColor: 'text-white/50',
    switcherActiveClass: () => 'bg-white text-gray-900',
    switcherInactiveClass: 'text-white/60 hover:text-white hover:bg-white/10',
  },

  /* ══ 粉紅少女 ══ */
  pink: {
    label: '粉紅',
    dot: 'bg-rose-300',
    bgImage: 'https://readdy.ai/api/search-image?query=ultra%20soft%20pastel%20kawaii%20Japanese%20cherry%20blossom%20sakura%20park%20daytime%2C%20dreamy%20pink%20petals%20falling%20gently%2C%20warm%20sunny%20spring%20morning%2C%20soft%20bokeh%20background%2C%20light%20airy%20cute%20feminine%20aesthetic%2C%20pale%20pink%20peach%20cream%20tones%2C%20romantic%20girly%20Instagrammable%20atmosphere%2C%20very%20bright%20high-key%20photography%2C%20minimal%20clean%20pastel%20wonderland%2C%20delicate%20flowers%20with%20soft%20white%20clouds&width=1080&height=1920&seq=hero-pink-kawaii-2026&orientation=portrait',
    overlay: 'bg-gradient-to-b from-pink-100/50 via-rose-50/35 to-pink-200/55',
    badgeBg: 'bg-white/85',
    badgeBorder: 'border-rose-200',
    badgeText: 'text-rose-500',
    badgeIcon: 'ri-heart-line',
    accentText: 'text-rose-500',
    titleText: 'text-gray-900',
    subText: 'text-rose-400',
    ctaPrimary: 'bg-rose-400 hover:bg-rose-500 text-white',
    ctaSecondary: 'bg-white/85 hover:bg-white text-rose-500 border border-rose-200',
    waitBorder: 'border-rose-200 hover:border-rose-300',
    waitBg: 'bg-white/80 hover:bg-white/92',
    waitTitle: 'text-gray-800',
    waitSub: 'text-gray-400',
    statsBg: 'bg-white/80',
    statsBorder: 'border-rose-100',
    statsDivide: 'divide-rose-100',
    statsNum: 'text-rose-500',
    statsLabel: 'text-rose-300',
    iconCardBg: 'bg-white/80',
    iconCardBorder: 'border-rose-100',
    iconCardHover: 'hover:bg-white',
    iconBg: 'bg-rose-400 group-hover:bg-rose-500',
    iconText: 'text-white',
    iconLabel: 'text-rose-500',
    featureCardClass: 'bg-white/82 border-rose-100 hover:bg-white hover:border-rose-200',
    featureTitleClass: 'text-gray-800',
    featureDescClass: 'text-rose-400/80',
    featureArrowClass: 'text-rose-300',
    sectionLabelClass: 'text-rose-300',
    switcherBg: 'bg-white/80 border-rose-200',
    switcherIconColor: 'text-rose-400',
    switcherActiveClass: () => 'bg-rose-400 text-white',
    switcherInactiveClass: 'text-rose-400 hover:text-rose-600 hover:bg-rose-50/70',
  },

  /* ══ 白色簡約 ══ */
  white: {
    label: '白色',
    dot: 'bg-gray-300',
    bgImage: 'https://readdy.ai/api/search-image?query=bright%20airy%20Japandi%20apartment%20bedroom%20with%20large%20windows%20soft%20morning%20light%2C%20lush%20green%20indoor%20plants%20hanging%20and%20on%20shelves%2C%20sage%20green%20and%20teal%20accent%20throw%20blanket%20on%20white%20bed%2C%20warm%20oak%20wood%20floors%2C%20minimal%20clean%20furniture%2C%20botanical%20fresh%20green%20tones%20throughout%2C%20soft%20natural%20diffused%20lighting%2C%20professional%20interior%20photography%2C%20serene%20calm%20tropical%20minimalist%20atmosphere%2C%20white%20walls%20with%20green%20plant%20accents&width=1080&height=1920&seq=white-teal-mobile-hero-2026&orientation=portrait',
    overlay: 'bg-gradient-to-b from-white/55 via-white/45 to-white/65',
    badgeBg: 'bg-teal-50',
    badgeBorder: 'border-teal-200',
    badgeText: 'text-teal-700',
    badgeIcon: 'ri-sparkling-2-fill',
    accentText: 'text-teal-600',
    titleText: 'text-gray-900',
    subText: 'text-gray-600',
    ctaPrimary: 'bg-teal-700 hover:bg-teal-800 text-white',
    ctaSecondary: 'bg-white hover:bg-teal-50 text-teal-700 border-2 border-teal-200',
    waitBorder: 'border-amber-500/50 hover:border-amber-500/80',
    waitBg: 'bg-white/80 hover:bg-white/95',
    waitTitle: 'text-gray-800',
    waitSub: 'text-gray-500',
    statsBg: 'bg-white/80',
    statsBorder: 'border-teal-100',
    statsDivide: 'divide-gray-200',
    statsNum: 'text-teal-600',
    statsLabel: 'text-gray-500',
    iconCardBg: 'bg-white/90',
    iconCardBorder: 'border-teal-100',
    iconCardHover: 'hover:bg-teal-50/40',
    iconBg: 'bg-teal-600/90 group-hover:bg-teal-600',
    iconText: 'text-white',
    iconLabel: 'text-teal-700',
    featureCardClass: 'bg-white/80 border-gray-200 hover:bg-teal-50/30 hover:border-teal-200',
    featureTitleClass: 'text-gray-800',
    featureDescClass: 'text-gray-500',
    featureArrowClass: 'text-teal-400',
    sectionLabelClass: 'text-gray-500',
    switcherBg: 'bg-white/80 border-gray-200',
    switcherIconColor: 'text-gray-400',
    switcherActiveClass: () => 'bg-teal-700 text-white',
    switcherInactiveClass: 'text-gray-500 hover:text-gray-800 hover:bg-gray-100/70',
  },
};

const THEME_ORDER: ThemeKey[] = ['tech', 'pink', 'white'];

/* ─── 功能卡片資料（所有主題統一） ─── */
const FEATURE_CARDS = [
  { to: '/ai-match', icon: 'ri-sparkling-2-fill', title: 'AI 智能媒合', desc: '系統自動分析配對，快速找到合適租客或房源', isNew: true },
  { to: '/landlord-dashboard', icon: 'ri-wallet-3-line', title: '我的房地產錢包', desc: '多房源管理、收益追蹤、市場分析一站搞定', isNew: true },
  { to: '/no-commission', icon: 'ri-money-dollar-circle-line', title: '零仲介費直租', desc: '屋主直接發布，租客直接聯繫，省下高額費用' },
  { to: '/ai-features', icon: 'ri-robot-2-line', title: 'AI 功能解析', desc: '看看這個平台的 AI 到底做了哪些事', isNew: true },
];

const RENTAL_NOTICE_KEY = 'rental_notice_agreed';

export default function MobileHero({ tenantMatchCount, onShowWaitingModal, onShowGuestOnboarding }: MobileHeroProps) {
  const { theme, setTheme } = useMobileTheme();
  const t = THEMES[theme];
  const [showRentalNotice, setShowRentalNotice] = useState(false);
  const navHeight = useNavHeight(16);

  // ── 每張背景圖的載入狀態，分別追蹤 ──
  const [loadedMap, setLoadedMap] = useState<Record<ThemeKey, boolean>>({
    tech: false,
    pink: false,
    white: false,
  });

  const markLoaded = (key: ThemeKey) => {
    setLoadedMap((prev) => ({ ...prev, [key]: true }));
  };

  const handleRentOut = () => {
    if (sessionStorage.getItem(RENTAL_NOTICE_KEY)) {
      onShowGuestOnboarding();
    } else {
      setShowRentalNotice(true);
    }
  };

  const handleNoticeAgree = () => {
    sessionStorage.setItem(RENTAL_NOTICE_KEY, '1');
    setShowRentalNotice(false);
    onShowGuestOnboarding();
  };

  return (
    <div className="relative min-h-screen flex flex-col overflow-hidden lg:hidden">

      {/* ── 底圖：三張圖疊加，用 opacity 切換，避免圖片重新載入 ── */}
      <div className="absolute inset-0">
        {/* 佔位色層：圖片尚未載入時顯示，避免閃白 */}
        <div
          className="absolute inset-0"
          style={{
            background: theme === 'tech'
              ? 'linear-gradient(160deg, #0f172a 0%, #134e4a 100%)'
              : theme === 'pink'
              ? 'linear-gradient(160deg, #fdf2f8 0%, #fce7f3 100%)'
              : 'linear-gradient(160deg, #f0fdf4 0%, #f0fdfa 100%)',
            transition: 'background 0.4s ease',
            opacity: loadedMap[theme] ? 0 : 1,
          }}
        />

        {/* 白色模式：強化遮罩讓文字清晰可讀 */}
        {theme === 'white' && (
          <div className="absolute inset-0 bg-gradient-to-b from-white/85 via-white/75 to-white/88" />
        )}
        {THEME_ORDER.map((key) => (
          <img
            key={key}
            src={THEMES[key].bgImage}
            alt=""
            aria-hidden="true"
            onLoad={() => markLoaded(key)}
            className="absolute inset-0 w-full h-full object-cover object-top"
            style={{
              opacity: theme === key && theme !== 'white' ? 1 : 0,
              filter: loadedMap[key] ? 'blur(0px)' : 'blur(20px)',
              transform: loadedMap[key] ? 'scale(1)' : 'scale(1.08)',
              transition: loadedMap[key]
                ? 'opacity 0.4s ease, filter 0.9s ease, transform 0.9s ease'
                : 'opacity 0.4s ease',
              willChange: 'opacity, filter, transform',
            }}
            loading={key === 'tech' ? 'eager' : 'lazy'}
          />
        ))}
        <div
          className="absolute inset-0"
          style={{
            background: theme === 'pink'
              ? 'linear-gradient(to bottom, rgba(251,207,232,0.5) 0%, rgba(255,241,242,0.35) 50%, rgba(251,207,232,0.55) 100%)'
              : theme === 'white'
              ? 'linear-gradient(to bottom, rgba(255,255,255,0.55) 0%, rgba(255,255,255,0.40) 50%, rgba(255,255,255,0.60) 100%)'
              : 'linear-gradient(to bottom, rgba(0,0,0,0.65) 0%, rgba(0,0,0,0.50) 50%, rgba(0,0,0,0.70) 100%)',
            transition: 'background 0.4s ease',
          }}
        />
      </div>

      {/* ── 主內容 ── */}
      <div className="relative z-10 flex flex-col w-full px-4 pb-10"
        style={{ paddingTop: navHeight }}>

        {/* ── 主題切換器（移除 backdrop-blur，改用實色半透明背景） ── */}
        <div className="flex justify-center mb-5">
          <div
            className="flex items-center gap-1 px-2.5 py-1.5 rounded-full border"
            style={{
              background: theme === 'tech' ? 'rgba(0,0,0,0.45)' : theme === 'pink' ? 'rgba(255,255,255,0.82)' : 'rgba(255,255,255,0.78)',
              borderColor: theme === 'tech' ? 'rgba(255,255,255,0.15)' : theme === 'pink' ? 'rgb(254,205,211)' : 'rgb(229,231,235)',
            }}
          >
            <i className={`ri-palette-line text-xs mr-0.5 ${t.switcherIconColor}`}></i>
            {THEME_ORDER.map((key) => {
              const isActive = theme === key;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => setTheme(key)}
                  style={{ touchAction: 'manipulation' }}
                  className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                    isActive ? t.switcherActiveClass(key) : t.switcherInactiveClass
                  }`}
                >
                  <span
                    className="w-2 h-2 rounded-full flex-shrink-0"
                    style={{ backgroundColor: THEMES[key].dot.replace('bg-', '') === THEMES[key].dot ? '#ccc' : undefined,
                      background: key === 'tech' ? '#2dd4bf' : key === 'pink' ? '#fb7185' : '#d1d5db'
                    }}
                  ></span>
                  {THEMES[key].label}
                </button>
              );
            })}
          </div>
        </div>

        {/* H1 主標題 */}
        <h1 className={`text-3xl sm:text-4xl font-black ${t.titleText} mb-3 leading-tight tracking-tight text-center`}>
          一個由AI媒合<br />
          <span className={t.accentText}>零仲介費</span><br />房地產平台
        </h1>

        {/* 副標題 */}
        <p className={`text-sm ${t.subText} mb-6 leading-relaxed text-center`}>
          免費刊登房源・直接聯繫・AI 智能配對
        </p>

        {/* 兩個主 CTA */}
        <div className="flex gap-3 mb-5 justify-center">
          <button
            type="button"
            onClick={handleRentOut}
            style={{ touchAction: 'manipulation' }}
            className={`flex-1 flex items-center justify-center gap-2 py-4 ${t.ctaPrimary} rounded-2xl font-bold text-sm cursor-pointer whitespace-nowrap`}
          >
            <i className="ri-home-4-line text-base"></i>我要出租
          </button>
          <Link
            to="/regions"
            style={{ touchAction: 'manipulation' }}
            className={`flex-1 flex items-center justify-center gap-2 py-4 ${t.ctaSecondary} rounded-2xl font-bold text-sm cursor-pointer whitespace-nowrap`}
          >
            <i className="ri-search-line text-base"></i>我要找房
          </Link>
        </div>

        {/* 往下滾動提示 */}
        <div className="flex flex-col items-center gap-2 mb-6">
          <div className={`flex items-center gap-2 px-5 py-2.5 rounded-full border-2 ${
            theme === 'pink'
              ? 'border-rose-300 bg-white/70 text-rose-500'
              : theme === 'white'
              ? 'border-teal-300/70 bg-white/80 text-teal-700'
              : 'border-teal-400/60 bg-white/10 text-white'
          }`}>
            <i className="ri-home-heart-line text-base"></i>
            <span className="text-sm font-bold tracking-wide whitespace-nowrap">往下看更多免仲介費物件</span>
            <i className="ri-arrow-down-double-line text-base animate-bounce"></i>
          </div>
        </div>

        {/* ── 平台功能卡片 ── */}
        <div className="max-w-sm mx-auto w-full">
          <p className={`text-xs font-semibold uppercase tracking-widest mb-3 text-center ${t.sectionLabelClass}`}>
            平台功能
          </p>
          <div className="grid grid-cols-2 gap-2.5">
            {FEATURE_CARDS.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                style={{ touchAction: 'manipulation' }}
                className={`flex flex-col items-start gap-2 px-3.5 py-3.5 rounded-2xl border cursor-pointer group ${t.featureCardClass}`}
              >
                <div className="flex items-center justify-between w-full">
                  <div className={`w-9 h-9 flex-shrink-0 flex items-center justify-center rounded-xl ${t.iconBg}`}>
                    <i className={`${item.icon} text-sm text-white`}></i>
                  </div>
                  {item.isNew && (
                    <span className={`text-[9px] font-black px-1.5 py-0.5 rounded-full leading-none whitespace-nowrap ${
                      theme === 'pink' ? 'bg-rose-400 text-white'
                      : theme === 'white' ? 'bg-teal-600 text-white'
                      : 'bg-teal-500 text-white'
                    }`}>NEW</span>
                  )}
                </div>
                <div>
                  <p className={`text-xs font-bold leading-snug ${t.featureTitleClass}`}>{item.title}</p>
                  <p className={`text-[11px] leading-snug mt-0.5 ${t.featureDescClass} line-clamp-2`}>{item.desc}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>

        <div className="h-10"></div>
      </div>

      {showRentalNotice && (
        <RentalNoticeModal onAgree={handleNoticeAgree} />
      )}
    </div>
  );
}
